import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { UserPlus, KeyRound, Trash2, PowerOff, Power, X, ShieldCheck, Briefcase, User } from "lucide-react";

const emptyForm = { username: "", name: "", password: "", email: "", phone: "", permissions: [], services: [], allowed_statuses: [] };

export default function Employees() {
  const [employees, setEmployees] = useState([]);
  const [meta, setMeta] = useState({ permissions: [], services: [], lead_statuses: [] });
  const [modal, setModal] = useState(null); // {mode:'create'|'edit'|'reset', user?}
  const [form, setForm] = useState(emptyForm);
  const [err, setErr] = useState("");

  const load = async () => {
    const [emp, m] = await Promise.all([api.get("/employees"), api.get("/employees/meta")]);
    setEmployees(emp.data);
    setMeta(m.data);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(emptyForm); setErr(""); setModal({ mode: "create" }); };
  const openEdit = (u) => {
    setForm({
      username: u.username, name: u.name, password: "", email: u.email || "", phone: u.phone || "",
      permissions: u.permissions || [], services: u.services || [], allowed_statuses: u.allowed_statuses || [],
    });
    setErr(""); setModal({ mode: "edit", user: u });
  };
  const openReset = (u) => { setForm({ ...emptyForm, password: "" }); setErr(""); setModal({ mode: "reset", user: u }); };
  const close = () => setModal(null);

  const togglePerm = (k) => setForm((f) => ({ ...f, permissions: f.permissions.includes(k) ? f.permissions.filter((x) => x !== k) : [...f.permissions, k] }));
  const toggleSvc = (s) => setForm((f) => ({ ...f, services: f.services.includes(s) ? f.services.filter((x) => x !== s) : [...f.services, s] }));
  const toggleStatus = (s) => setForm((f) => ({ ...f, allowed_statuses: f.allowed_statuses.includes(s) ? f.allowed_statuses.filter((x) => x !== s) : [...f.allowed_statuses, s] }));

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    try {
      if (modal.mode === "create") {
        await api.post("/employees", form);
      } else if (modal.mode === "edit") {
        const { name, email, phone, permissions, services, allowed_statuses } = form;
        await api.patch(`/employees/${modal.user.id}`, { name, email, phone, permissions, services, allowed_statuses });
      } else {
        await api.post(`/employees/${modal.user.id}/reset-password`, { new_password: form.password });
      }
      close(); load();
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Failed");
    }
  };

  const toggleDisable = async (u) => { await api.patch(`/employees/${u.id}`, { disabled: !u.disabled }); load(); };
  const remove = async (u) => {
    if (!window.confirm(`Delete employee "${u.username}"? Any assigned leads will be unassigned.`)) return;
    await api.delete(`/employees/${u.id}`); load();
  };

  const permLabel = (k) => k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <div className="space-y-6" data-testid="employees-page">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-heading text-3xl font-extrabold">Process Management</h1>
          <p className="text-sm text-muted-foreground">Grant per-module and per-service access to internal staff</p>
        </div>
        <button className="btn btn-primary" onClick={openCreate} data-testid="btn-add-employee"><UserPlus size={16} /> New Process User</button>
      </div>

      <div className="card overflow-hidden">
        <table className="table-clean">
          <thead>
            <tr><th>Username</th><th>Name</th><th>Email</th><th>Permissions</th><th>Services</th><th>Assigned</th><th>Status</th><th className="text-right">Actions</th></tr>
          </thead>
          <tbody data-testid="employees-table-body">
            {employees.length === 0 && <tr><td colSpan={8} className="text-center text-muted-foreground py-8">No employees yet. Create the first one above.</td></tr>}
            {employees.map((u) => (
              <tr key={u.id}>
                <td className="font-mono font-semibold">{u.username}</td>
                <td>{u.name}</td>
                <td className="text-muted-foreground">{u.email || "—"}</td>
                <td>
                  <div className="flex flex-wrap gap-1 max-w-[220px]">
                    {(u.permissions || []).length === 0 && <span className="text-xs text-muted-foreground">No perms</span>}
                    {(u.permissions || []).map((p) => <span key={p} className="badge badge-blue text-[10px]">{permLabel(p)}</span>)}
                  </div>
                </td>
                <td>
                  <div className="flex flex-wrap gap-1 max-w-[180px]">
                    {(u.services || []).length === 0 ? <span className="badge badge-gray text-[10px]">All</span> :
                      (u.services || []).map((s) => <span key={s} className="badge badge-green text-[10px]">{s}</span>)}
                  </div>
                </td>
                <td className="tabular font-semibold">{u.assigned_lead_count}</td>
                <td>{u.disabled ? <span className="badge badge-red">Disabled</span> : <span className="badge badge-green">Active</span>}</td>
                <td className="text-right space-x-1 whitespace-nowrap">
                  <Link to={`/employees/${u.id}`} className="btn btn-ghost !p-2 inline-flex" title="Profile & performance" data-testid={`emp-profile-${u.username}`}><User size={16} /></Link>
                  <button className="btn btn-ghost !p-2" onClick={() => openEdit(u)} title="Edit" data-testid={`emp-edit-${u.username}`}><ShieldCheck size={16} /></button>
                  <button className="btn btn-ghost !p-2" onClick={() => openReset(u)} title="Reset password" data-testid={`emp-reset-${u.username}`}><KeyRound size={16} /></button>
                  <button className="btn btn-ghost !p-2" onClick={() => toggleDisable(u)} title={u.disabled ? "Enable" : "Disable"} data-testid={`emp-toggle-${u.username}`}>{u.disabled ? <Power size={16} /> : <PowerOff size={16} />}</button>
                  <button className="btn btn-ghost !p-2 text-red-600" onClick={() => remove(u)} title="Delete" data-testid={`emp-delete-${u.username}`}><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={close}>
          <div className="card p-6 w-full max-w-xl max-h-[92vh] overflow-y-auto fade-in" onClick={(e) => e.stopPropagation()} data-testid="emp-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg">
                {modal.mode === "create" ? "New Process User" : modal.mode === "edit" ? `Edit ${modal.user.username}` : `Reset password for ${modal.user.username}`}
              </h3>
              <button className="btn btn-ghost !p-1.5" onClick={close}><X size={16} /></button>
            </div>
            <form onSubmit={submit} className="space-y-3">
              {modal.mode === "create" && (
                <div>
                  <label className="text-xs font-semibold text-muted-foreground">Username *</label>
                  <input required className="input mt-1" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} data-testid="emp-modal-username" />
                </div>
              )}
              {modal.mode !== "reset" && (
                <>
                  <div><label className="text-xs font-semibold text-muted-foreground">Full Name *</label>
                    <input required className="input mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="emp-modal-name" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="text-xs font-semibold text-muted-foreground">Email</label>
                      <input type="email" className="input mt-1" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                    <div><label className="text-xs font-semibold text-muted-foreground">Phone</label>
                      <input className="input mt-1" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-muted-foreground flex items-center gap-1"><ShieldCheck size={12} /> Permissions</label>
                    <div className="mt-2 grid grid-cols-2 gap-2" data-testid="emp-modal-permissions">
                      {meta.permissions.map((p) => (
                        <label key={p} className="flex items-center gap-2 p-2 rounded-md border border-border cursor-pointer hover:bg-muted/50">
                          <input type="checkbox" checked={form.permissions.includes(p)} onChange={() => togglePerm(p)} data-testid={`perm-${p}`} />
                          <span className="text-sm">{permLabel(p)}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-muted-foreground flex items-center gap-1"><Briefcase size={12} /> Services (empty = all)</label>
                    <div className="mt-2 flex flex-wrap gap-2" data-testid="emp-modal-services">
                      {meta.services.map((s) => (
                        <label key={s} className={`px-3 py-1.5 rounded-full border cursor-pointer text-sm ${form.services.includes(s) ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted/50"}`}>
                          <input type="checkbox" className="hidden" checked={form.services.includes(s)} onChange={() => toggleSvc(s)} data-testid={`svc-${s}`} />
                          {s}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-muted-foreground flex items-center gap-1"><ShieldCheck size={12} /> Allowed Status Changes (empty = none)</label>
                    <div className="mt-2 flex flex-wrap gap-2" data-testid="emp-modal-statuses">
                      {(meta.lead_statuses || []).map((s) => (
                        <label key={s} className={`px-3 py-1.5 rounded-full border cursor-pointer text-xs ${form.allowed_statuses.includes(s) ? "bg-emerald-500 text-white border-emerald-500" : "border-border hover:bg-muted/50"}`}>
                          <input type="checkbox" className="hidden" checked={form.allowed_statuses.includes(s)} onChange={() => toggleStatus(s)} data-testid={`status-${s.replace(/\s+/g, "-")}`} />
                          {s}
                        </label>
                      ))}
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-1">This Process user can only move a lead into the statuses ticked here.</div>
                  </div>
                </>
              )}
              {(modal.mode === "create" || modal.mode === "reset") && (
                <div><label className="text-xs font-semibold text-muted-foreground">{modal.mode === "reset" ? "New Password *" : "Password *"}</label>
                  <input required type="password" className="input mt-1" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="emp-modal-password" /></div>
              )}
              {err && <div className="text-sm text-red-600" data-testid="emp-modal-error">{err}</div>}
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" className="btn btn-outline" onClick={close}>Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="emp-modal-submit">
                  {modal.mode === "create" ? "Create" : modal.mode === "edit" ? "Save" : "Reset"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
